import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';

/**
 * Reveal the first slide on panel search product cards. Slideshows connect while
 * the parent <dialog> is closed (display:none), so Fabric's IntersectionObserver
 * leaves every slide aria-hidden until the panel opens or the user hovers.
 *
 * @param {ParentNode} [root]
 */
export function refreshPanelSearchSlideshows(root = document) {
  const scope = root instanceof Element || root instanceof Document ? root : document;

  scope.querySelectorAll('.right-panel__search-suggestions-grid slideshow-component').forEach((slideshow) => {
    slideshow.setAttribute('in-viewport', '');

    const slides = [...slideshow.querySelectorAll('slideshow-slide')].filter(
      (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal')
    );

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    });

    if (typeof slideshow.select === 'function') {
      slideshow.select(0, undefined, { animate: false });
    }
  });
}

/** @returns {string} */
function getPredictiveSearchSectionId() {
  const sectionEl = document.querySelector('.shopify-section.predictive-search-section');
  if (sectionEl?.id) return normalizeSectionId(sectionEl.id);
  return 'predictive-search';
}

/** @returns {string} */
function getPanelSearchSuggestionsSectionId() {
  const sectionEl = document.querySelector('.shopify-section.panel-search-suggestions-section');
  if (sectionEl?.id) return normalizeSectionId(sectionEl.id);
  return 'panel-search-suggestions';
}

/** @type {string | null} */
let cachedIdleSuggestionsHTML = null;

/** @type {Promise<string | null> | null} */
let idleSuggestionsFetch = null;

/**
 * Idle suggestions are server-rendered in the hidden header section.
 * @returns {string | null}
 */
function readIdleSuggestionsFromDom() {
  const suggestions = document.querySelector(
    '.shopify-section.panel-search-suggestions-section .right-panel__search-suggestions',
  );
  return suggestions?.outerHTML ?? null;
}

/** @returns {string | null} */
function syncIdleSuggestionsCache() {
  const fromDom = readIdleSuggestionsFromDom();
  if (fromDom) cachedIdleSuggestionsHTML = fromDom;
  return cachedIdleSuggestionsHTML;
}

/**
 * @param {Document} parsed
 * @returns {HTMLElement | null}
 */
function extractPanelIdleSuggestions(parsed) {
  return (
    parsed.querySelector('.right-panel__search-suggestions') ||
    parsed.querySelector('.shopify-section')?.querySelector('.right-panel__search-suggestions') ||
    null
  );
}

/**
 * @returns {Promise<string | null>}
 */
export function prefetchPanelSearchIdleSuggestions() {
  const cached = syncIdleSuggestionsCache();
  if (cached) return Promise.resolve(cached);

  if (idleSuggestionsFetch) return idleSuggestionsFetch;

  idleSuggestionsFetch = sectionRenderer
    .getSectionHTML(getPanelSearchSuggestionsSectionId(), true)
    .then((markup) => {
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const suggestions = extractPanelIdleSuggestions(parsed);
      const html = suggestions?.outerHTML ?? null;
      if (html) cachedIdleSuggestionsHTML = html;
      return html;
    })
    .catch((error) => {
      console.error(error);
      return null;
    })
    .finally(() => {
      idleSuggestionsFetch = null;
    });

  return idleSuggestionsFetch;
}

/** Default live-search limit — overridden by data-results-limit on the component. */
const PANEL_SEARCH_RESULTS_LIMIT_DEFAULT = 12;

/** Delay before showing loading state — avoids flicker on fast responses */
const SEARCH_LOADING_DELAY_MS = 100;

/** Minimum characters before live search fetches (debounced input) */
const SEARCH_MIN_QUERY_LENGTH = 2;

/**
 * Live search for the right-panel search UI.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLFormElement} searchForm
 * @property {HTMLElement} searchSuggestions
 * @extends {Component<Refs>}
 */
class PanelSearchComponent extends Component {
  requiredRefs = ['searchInput', 'searchForm', 'searchSuggestions'];

  /** @type {string} */
  #initialSuggestionsHTML = '';

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {AbortController} */
  #listenerController = new AbortController();

  /** @type {ReturnType<typeof setTimeout> | null} */
  #searchLoadingTimer = null;

  /** @type {boolean} */
  #emptySuggestionsLoaded = false;

  /** @type {Promise<void> | null} */
  #idleLoadPromise = null;

  /**
   * Server-rendered idle suggestions (load_empty_state) — skip async idle fetch.
   * @returns {boolean}
   */
  #captureServerRenderedSuggestions() {
    if (this.#initialSuggestionsHTML) return true;

    const wrap = this.refs.searchSuggestions;
    const serverProducts = wrap.querySelector('.predictive-search-results__products');
    if (!serverProducts) return false;

    this.#initialSuggestionsHTML = wrap.innerHTML;
    this.#emptySuggestionsLoaded = true;
    return true;
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#listenerController;

    if (this.#captureServerRenderedSuggestions()) {
      requestAnimationFrame(() => refreshPanelSearchSlideshows(this.refs.searchSuggestions));
    } else if (this.#needsIdleSuggestions()) {
      void this.#loadEmptySuggestions();
    } else if (!this.#initialSuggestionsHTML) {
      this.#initialSuggestionsHTML = this.refs.searchSuggestions.innerHTML;
      this.#emptySuggestionsLoaded = true;
      requestAnimationFrame(() => refreshPanelSearchSlideshows(this.refs.searchSuggestions));
    }

    this.refs.searchInput.addEventListener('input', this.#handleInput, { signal });
    this.refs.searchForm.addEventListener('submit', this.onFormSubmit, { signal });
    this.refs.searchInput.addEventListener('keydown', this.onSearchKeyDown, { signal });

    const term = this.refs.searchInput.value.trim();
    if (term.length >= SEARCH_MIN_QUERY_LENGTH) {
      this.#getSearchResults(term);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listenerController.abort();
    this.#listenerController = new AbortController();
  }

  #handleInput = () => {
    this.search();
  };

  search = debounce(() => {
    const searchTerm = this.refs.searchInput.value.trim();

    if (!searchTerm.length) {
      this.#resetSuggestions();
      return;
    }

    if (searchTerm.length < SEARCH_MIN_QUERY_LENGTH) {
      this.#createAbortController();
      this.#endSearching();
      this.#resetSuggestions();
      return;
    }

    this.#getSearchResults(searchTerm);
  }, 200);

  onFormSubmit = (event) => {
    event.preventDefault();
    const searchTerm = this.refs.searchInput.value.trim();
    if (searchTerm.length) {
      this.search.cancel?.();
      this.#getSearchResults(searchTerm);
    }
  };

  onSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const searchTerm = this.refs.searchInput.value.trim();
      if (searchTerm.length) {
        this.search.cancel?.();
        this.#getSearchResults(searchTerm);
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.refs.searchInput.value = '';
      this.search.cancel?.();
      this.#resetSuggestions();
    }
  };

  async #getSearchResults(searchTerm) {
    const sectionId = getPredictiveSearchSectionId();
    const resultsLimit = this.#getResultsLimit();

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(resultsLimit));
    url.searchParams.set('resources[limit_scope]', 'each');

    const abortController = this.#createAbortController();
    this.#beginSearching();

    try {
      const resultsMarkup = await sectionRenderer.getSectionHTML(sectionId, false, url, abortController.signal);
      this.#endSearching();
      if (abortController.signal.aborted || !resultsMarkup) return;

      this.#applySearchResultsMarkup(resultsMarkup);
    } catch (error) {
      this.#endSearching();
      if (abortController.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) return;
      throw error;
    }
  }

  #beginSearching() {
    if (this.#searchLoadingTimer) {
      clearTimeout(this.#searchLoadingTimer);
      this.#searchLoadingTimer = null;
    }

    if (this.refs.searchSuggestions.classList.contains('is-searching')) return;

    this.#searchLoadingTimer = setTimeout(() => {
      this.#searchLoadingTimer = null;
      this.refs.searchSuggestions.classList.add('is-searching');
      this.refs.searchSuggestions.setAttribute('aria-busy', 'true');
    }, SEARCH_LOADING_DELAY_MS);
  }

  #endSearching() {
    if (this.#searchLoadingTimer) {
      clearTimeout(this.#searchLoadingTimer);
      this.#searchLoadingTimer = null;
    }

    this.refs.searchSuggestions.classList.remove('is-searching');
    this.refs.searchSuggestions.removeAttribute('aria-busy');
  }

  /**
   * @param {string} markup
   */
  #applySearchResultsMarkup(markup) {
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const newResults =
      parsed.querySelector('#predictive-search-results') ||
      parsed.querySelector('.shopify-section')?.querySelector('#predictive-search-results');

    if (!newResults) return;

    const wrap = this.refs.searchSuggestions;
    const currentResults = wrap.querySelector('#predictive-search-results');

    if (currentResults) {
      currentResults.setAttribute('data-live-update', '');
      morph(currentResults, newResults.outerHTML);
      currentResults.removeAttribute('data-live-update');
    } else {
      wrap.innerHTML = newResults.outerHTML;
    }
  }

  #resetSuggestions() {
    this.#endSearching();
    this.#createAbortController();

    const idleHtml = this.#initialSuggestionsHTML || syncIdleSuggestionsCache();
    if (idleHtml) {
      this.refs.searchSuggestions.innerHTML = idleHtml;
      if (!this.#initialSuggestionsHTML) {
        this.#initialSuggestionsHTML = idleHtml;
        this.#emptySuggestionsLoaded = true;
      }
      requestAnimationFrame(() => refreshPanelSearchSlideshows(this.refs.searchSuggestions));
      return;
    }

    void this.#loadEmptySuggestions();
  }

  ensureIdleSuggestions() {
    if (this.#captureServerRenderedSuggestions()) return Promise.resolve();
    return this.#loadEmptySuggestions();
  }

  /** Re-run live search when the panel opens with an existing query. */
  syncActiveQuery() {
    const searchTerm = this.refs.searchInput.value.trim();
    if (searchTerm.length >= SEARCH_MIN_QUERY_LENGTH) {
      this.search.cancel?.();
      void this.#getSearchResults(searchTerm);
    }
  }

  /**
   * @param {string} html
   */
  #applyIdleSuggestionsHTML(html) {
    if (this.refs.searchInput.value.trim().length >= SEARCH_MIN_QUERY_LENGTH) return;
    if (this.refs.searchSuggestions.querySelector('.predictive-search-results__products')) return;

    this.#emptySuggestionsLoaded = true;
    this.refs.searchSuggestions.innerHTML = html;
    this.#initialSuggestionsHTML = html;
    this.refs.searchSuggestions.removeAttribute('aria-busy');
    this.refs.searchSuggestions.classList.remove('is-searching');
    requestAnimationFrame(() => refreshPanelSearchSlideshows(this.refs.searchSuggestions));
  }

  #loadEmptySuggestions() {
    if (this.#captureServerRenderedSuggestions()) return Promise.resolve();
    if (this.#emptySuggestionsLoaded && this.#initialSuggestionsHTML) return Promise.resolve();

    if (this.#idleLoadPromise) return this.#idleLoadPromise;

    this.#idleLoadPromise = this.#fetchAndApplyIdleSuggestions().finally(() => {
      this.#idleLoadPromise = null;
    });

    return this.#idleLoadPromise;
  }

  async #fetchAndApplyIdleSuggestions() {
    if (this.#captureServerRenderedSuggestions()) return;
    if (this.#emptySuggestionsLoaded && this.#initialSuggestionsHTML) return;

    this.refs.searchSuggestions.setAttribute('aria-busy', 'true');

    const html = syncIdleSuggestionsCache() ?? (await prefetchPanelSearchIdleSuggestions());

    if (this.refs.searchInput.value.trim().length >= SEARCH_MIN_QUERY_LENGTH) {
      this.refs.searchSuggestions.removeAttribute('aria-busy');
      return;
    }

    if (!html) {
      this.refs.searchSuggestions.removeAttribute('aria-busy');
      return;
    }

    this.#applyIdleSuggestionsHTML(html);
  }

  #needsIdleSuggestions() {
    if (this.refs.searchSuggestions.querySelector('.right-panel__search-suggestions')) return false;
    return !this.refs.searchSuggestions.querySelector('.predictive-search-results__products, .predictive-search-results__no-results');
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  /** @returns {number} */
  #getResultsLimit() {
    const limit = Number.parseInt(this.dataset.resultsLimit ?? '', 10);
    return Number.isFinite(limit) && limit > 0 ? limit : PANEL_SEARCH_RESULTS_LIMIT_DEFAULT;
  }
}

if (!customElements.get('panel-search-component')) {
  customElements.define('panel-search-component', PanelSearchComponent);
}
